<template>
  <div>
    <img src="~/assets/images/integrations/square.png" class="max-h-8 max-w-14 opacity-85 mb-2 mt-4">

    <o-alert v-if="isStatusAuthError" show :title="$t('integration.needsReconnecting')" class="text-sm" variant="warning">
      <p>
        {{ $t('integration.square.reconnectInfo') }}
      </p>
    </o-alert>

    <o-alert v-if="isStatusSetupIncomplete" show :title="$t('integration.setupIncomplete')" class="text-sm" variant="warning">
      <p>
        {{ $t('integration.square.setupIncompleteInfo') }}
      </p>
    </o-alert>

    <form id="payment-form">
      <div id="card-container" />
    </form>

    <div id="payment-status-container" />

    <template v-if="isStatusConnected">
      <div v-if="!loaded && !error" class="w-full text-center my-6">
        <o-loader :label="$t('integration.square.loading')" size="20px" class="mx-auto mb-3" />
        <p class="text-sm text-gray-700 dark:text-gray-200">
          {{ $t('integration.square.loading') }}
        </p>
      </div>

      <div v-if="error" class="w-full my-3 text-sm">
        <o-alert variant="danger" :title="error" show />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
export default {
  guid: 'b0bca8b297674792a6f4489d272b8230',
}
</script>

<script setup lang="ts">
import * as Sentry from '@sentry/nuxt'
import {
  OAlert,
} from '@/components/elements'

interface Props {
  integration: any
  customer: any
}

const props = defineProps<Props>()

const { $config, $i18n } = useNuxtApp()

const cardInstance = ref<any>(null)
const loaded = ref(false)
const error = ref<string | false>(false)

const isStatusDisconnected = computed(() => props.integration.isStatusDisconnected)
const isStatusConnected = computed(() => props.integration.isStatusConnected)
const isStatusSetupIncomplete = computed(() => props.integration.isStatusSetupIncomplete)
const isStatusAuthError = computed(() => props.integration.isStatusAuthError)

onMounted(() => {
  nextTick(() => {
    if (isStatusConnected.value) {
      load()
    }
  })
})

// Load Square Web Payment SDK script if not already loaded
const load = () => {
  if ((window as any).Square) {
    nextTick(() => {
      setup()
    })
  } else {
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = ($config as any).public.instance === 'production' ? 'https://web.squarecdn.com/v1/square.js' : 'https://sandbox.web.squarecdn.com/v1/square.js'
    document.body.appendChild(script)

    script.onload = () => {
      nextTick(() => {
        setup()
      })
    }
  }
}

// Get Square integration ready
const setup = async () => {
  const appId = props.integration.data.authData.applicationID
  const locationId = props.integration.data.params.location_id

  try {
    // Initialise Web Payments SDK
    const payments = (window as any).Square.payments(appId, locationId, {
      scriptSrc: ($config as any).public.instance === 'production' ? 'https://web.squarecdn.com/v1/square.js' : 'https://sandbox.web.squarecdn.com/v1/square.js'
    })

    // Initialise Card payment method
    const card = await initializeCard(payments)

    cardInstance.value = card
    loaded.value = true
  } catch (e) {
    console.log(e)
    error.value = $i18n.t('integration.square.error') as string
  }
}

// Initialise Card payment method and attach to DOM
const initializeCard = async (payments: any) => {
  const card = await payments.card({
    style: {
      input: {
        fontSize: '13px',
      },
    },
  })

  await card.attach('#card-container')

  return card
}

// Tokenize payment method ready to be sent to server for transaction
const process = async () => {
  try {
    const verificationDetails = {
      intent: 'STORE',
      billingContact: {
        familyName: props.customer.lastName || '',
        givenName: props.customer.firstName || '',
        email: props.customer.email || '',
        addressLines: [props.customer.address || ''],
        city: props.customer.city || '',
        region: props.customer.state || '',
        countryCode: props.customer.country || '',
      },
      customerInitiated: true,
      sellerKeyedIn: false
    }

    console.log(verificationDetails)

    const tokenResult = await cardInstance.value.tokenize(verificationDetails)

    console.log(tokenResult)

    if (tokenResult.status === 'OK') {
      return { squareToken: tokenResult.token }
    } else if (tokenResult.status === 'Cancel') {
      Sentry.captureException(tokenResult)

      const errorMessage = 'Payment was cancelled by the user'

      throw new Error(errorMessage)
    } else {
      Sentry.captureException(tokenResult)

      let errorMessage = `Tokenization failed with status: ${tokenResult.status}`

      if (tokenResult.errors) {
        errorMessage += ` and errors: ${JSON.stringify(tokenResult.errors)}`
      }

      throw new Error(errorMessage)
    }
  } catch (e) {
    console.error('Payment processing error:', e)
    const errorMessage = 'Something went wrong while processing the payment.'
    throw new Error(errorMessage)
  }
}

defineExpose({ process })
</script>
